import type { JSX } from 'react';
import type { Callback } from 'ts/base/Callback';
import { useUserPermissionInfo } from 'ts/base/hooks/PermissionInfoHook';
import { useProjectId } from 'ts/base/hooks/UseProject';
import { SIDEBAR_SECTIONS, SidebarSection } from 'ts/base/perspective/sidebar/right/RightSidebar';
import { TeamscaleLink } from 'ts/base/routing/TeamscaleLink';
import type { PendingFinding } from 'ts/commons/code/RenderedCode.context';
import { Links } from 'ts/commons/links/Links';
import { Markdown } from 'ts/commons/markdown/Markdown';
import { PathUtils } from 'ts/commons/PathUtils';
import { Button } from 'ts/components/Button';
import { Icon } from 'ts/components/Icon';
import { Loader } from 'ts/components/Loader';
import { Popup } from 'ts/components/Popup';
import { Table, TableBody, TableCell, TableRow } from 'ts/components/Table';
import { BlacklistIcon } from 'ts/perspectives/findings/list/FindingsTable.columns';
import { EProjectPermission } from 'typedefs/EProjectPermission';
import { ETrafficLightColor } from 'typedefs/ETrafficLightColor';
import type { ExtendedTrackedFinding } from 'typedefs/ExtendedTrackedFinding';
import styles from './SidebarFindings.module.less';

type SidebarFindingsProps = {
	findings: Array<ExtendedTrackedFinding | PendingFinding>;
	onAddAllToTask: () => void;
	onLineNumberClicked: (lineNumber: number) => void;
};

/** The finding type id for a manually created review finding. */
export const REVIEW_FINDING_TYPE_ID = 'Code Review/Review Comment';

/** Component for creating the findings table in the sidebar. */
export function FindingsSidebarSection({
	findings,
	onLineNumberClicked,
	onAddAllToTask
}: SidebarFindingsProps): JSX.Element {
	const projectId = useProjectId();
	const permissionInfo = useUserPermissionInfo();
	const mayEditTasks = permissionInfo.hasProjectPermission(projectId, EProjectPermission.EDIT_TASKS);
	return (
		<SidebarSection {...SIDEBAR_SECTIONS.FINDINGS}>
			{findings.length > 0 ? (
				<>
					{mayEditTasks ? (
						<div className={styles.buttonContainer}>
							<Button basic compact size="small" onClick={onAddAllToTask} content="Add all to task" />
						</div>
					) : null}
					<Table className={styles.tableContainer}>
						<FindingTableBody values={findings} onLineNumberClicked={onLineNumberClicked} />
					</Table>
				</>
			) : (
				<div className="mt-2.5 text-center">
					<Icon name="trophy" className="green" /> Great, this file is free of findings!
				</div>
			)}
		</SidebarSection>
	);
}

function FindingTableBody({
	values,
	onLineNumberClicked
}: {
	values: Array<ExtendedTrackedFinding | PendingFinding>;
	onLineNumberClicked: Callback<number>;
}): JSX.Element {
	const cellClasses = `finding-message ${styles.cellLineBreak || ''}`;
	return (
		<TableBody>
			{values.map(finding => (
				<TableRow key={finding.id || finding.message}>
					<TableCell className={cellClasses}>
						<FindingMessage finding={finding} />
					</TableCell>
					<TableCell className="finding-location">
						<FindingLines onLineNumberClicked={onLineNumberClicked} finding={finding} />
					</TableCell>
				</TableRow>
			))}
		</TableBody>
	);
}

function FindingLines({
	finding,
	onLineNumberClicked
}: {
	finding: ExtendedTrackedFinding | PendingFinding;
	onLineNumberClicked: Callback<number>;
}): JSX.Element | null {
	if (!PathUtils.isTextRegionLocation(finding.location)) {
		return null;
	}
	const { rawStartLine, rawEndLine } = finding.location;

	const linkContent = formatFindingLocation(rawStartLine, rawEndLine);

	return (
		<button
			type="button"
			onClick={() => onLineNumberClicked(rawStartLine)}
			className={`${String(styles.findingsLink)} a-ref-line link teamscale-link-button`}
		>
			{linkContent}
		</button>
	);
}

function FindingMessage({ finding }: { finding: ExtendedTrackedFinding | PendingFinding }): JSX.Element {
	return (
		<>
			{finding.analysisTimestamp > 0 ? <ReviewComment finding={finding} /> : null}
			<BlacklistIcon blacklistInfo={'blacklistInfo' in finding ? finding.blacklistInfo : null} />
			<Icon
				color={
					finding.assessment === ETrafficLightColor.YELLOW.name
						? 'yellow'
						: finding.assessment === ETrafficLightColor.RED.name
							? 'red'
							: undefined
				}
				name="exclamation triangle"
			/>{' '}
			<FindingLink finding={finding} />
		</>
	);
}

function ReviewComment({ finding }: { finding: ExtendedTrackedFinding | PendingFinding }): JSX.Element {
	if (finding.typeId === REVIEW_FINDING_TYPE_ID) {
		return <Popup content="This is a code review finding" trigger={<Icon className="edit" />} />;
	}
	return (
		<Popup
			content="This is an external finding which was uploaded to Teamscale"
			trigger={<Icon className="moon" />}
		/>
	);
}

function FindingLink({ finding }: { finding: ExtendedTrackedFinding | PendingFinding }): JSX.Element {
	const projectId = useProjectId();
	const { typeId, message, id } = finding;

	return (
		<>
			{typeId === REVIEW_FINDING_TYPE_ID && id === '' ? (
				<>
					<Markdown inline markdownText={message} />{' '}
					<Loader
						active
						inline
						size="tiny"
						className="pending-review"
						title="Creation of the review finding is in progress"
					/>
				</>
			) : (
				<TeamscaleLink to={Links.findingDetails(projectId, id)}>
					<Markdown inline markdownText={message} />
				</TeamscaleLink>
			)}
		</>
	);
}

function formatFindingLocation(rawStartLine: number, rawEndLine?: number) {
	if (rawEndLine != null && rawStartLine !== rawEndLine) {
		return `${String(rawStartLine)}-${String(rawEndLine)}`;
	}
	return rawStartLine;
}
